import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { Component } from '../../decorators/component.decorator';
import { resolveVideoHost } from '../../global/helpers';
import { EventHandler } from '../../global/utils.type';
import type { UISurfaceColor } from '../../types/color.type';
import { TranslationLabels } from '../../types/labels.type';
import { UIAspect } from '../aspect/ui-aspect';
import { UIButton } from '../button/ui-button';
import { Labels } from '../../global/labels';
import { createElement } from '../../global/ui-helpers';
import type { AspectRatioInput } from '../aspect/ui-aspect.type';
import { UIElement } from '../ui-element';
import { UICardLayout, UICardLayouts } from './card.types';
import stylesCallback from './ui-card-callback.css';
import stylesColumn from './ui-card-column.css';
import stylesStats from './ui-card-stats.css';
import styles from './ui-card.css';
import { CmsAttribute } from '../../decorators/cms-attribute.decorator';
import { CMS_VALUE_TYPES, CMS_CONTENT_MODES } from '../../cms';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UICard
 * @element ui-card
 * @classdesc Represents a class for <code>ui-card</code> element.
 * Can be used for news with picture (or without) text and link.
 * @property {string} [image] {@attr image} Image URL for card.
 * @property {string} [alt=""] {@attr alt} Alternate text for image.
 * Same as alt for IMG.
 * @property {boolean} [columnCard] {@attr column-card} Enables column card styles.
 * @property {boolean} [callbackCard] {@attr callback-card} Enables callback card styles.
 * @property {boolean} [statCard] {@attr stat-card} Enables card style for showing off stats and figures.
 * @property {UICardLayout} [layout] {@attr layout} Position of the image.
 *  {@desc left}
 *  {@desc right}
 *  {@desc top} Only for column-card="true"
 *  {@desc bottom} Only for column-card="true"
 * @property {boolean} [disableSurface] {@attr disable-surface} Disables surface color and removes padding.
 * Only column-card="true"
 * @property {"left" | "center"} [alignContent] {@attr align-content} Position of the content.
 *  {@desc left}
 *  {@desc center}
 * Only column-card="true"
 * @property {"small" | "large"} [imageSize] {@attr image-size} Size of the image.
 *  {@desc small}
 *  {@desc large}
 * Only callback-card="true"
 * @property {string} [overline] {@attr overline} Overline text to be displayed above the title.
 * Only column-card="true"
 * @property {string} [aspectRatio] {@attr aspect-ratio} Aspect ratio for image, format "width:height", e.g. "16:9".
 * Only column-card="true"
 * @property {UISurfaceColor} [background] {@attr background} Sets background surface color for the offer.
 *  {@desc neutral: Neutral color - Default}
 *  {@desc primary: Primary color}
 *  {@desc secondary: Secondary color}
 *  {@desc tertiary: Tertiary color}
 *  {@desc outlined: Outlined color}
 * Only column-card="true"
 * @property {boolean} [imageNocrop] {@attr image-nocrop} Adds spacing between the image and the edge of the offer.
 * Only column-card="true"
 * @property {boolean} [spotlight] {@attr spotlight} Toggles spotlight styles for image.
 * Only column-card="true"
 * @property {boolean} [closable] {@attr closable} Renders a close button that dismisses the card when clicked.
 * Only callback-card="true"
 * @property {boolean} [livingOak] {@attr living-oak} Toggles living oak mask styles for image.
 * @property {string} label {@attr label} Header of the card.
 * @slot
 * @example
 * <ui-card image="assets/mocks/images/card01.jpg" label="A card for child – free for one year!" alt="Family">
 *     <ui-badge>Youth</ui-badge>
 *     <time>04.10.2024</time>
 *     <p>Order the Swedbank ISIC card on the special offer right here via Internet Banking
 *         in just a few minutes and have it delivered by post.</p>
 *     <ui-link layout="primary" href="javascript:console.log('Clicked')">ISIC student card</ui-link>
 * </ui-card>
 */
@Component({
    tag: 'ui-card',
    styles: [...styles, ...stylesColumn, ...stylesStats, ...stylesCallback],
    cms: {
        mode: CMS_CONTENT_MODES.Children,
    },
})
export class UICard extends UIElement {
    static readonly Layout: UICardLayouts = {
        Left: 'left',
        Right: 'right',
        Top: 'top',
        Bottom: 'bottom',
    };

    static readonly CloseEventName = 'card-close';

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-card', {
            close: 'Close',
        });
    }

    @Attribute(String) label?: string;
    @CmsAttribute({ valueType: CMS_VALUE_TYPES.Url })
    @Attribute(String)
    image?: string;
    @Attribute(String) alt: string = '';
    @Attribute(Boolean) disableSurface?: boolean;
    @Attribute(Boolean) imageNocrop?: boolean;
    @Attribute(Boolean) spotlight?: boolean;
    @Attribute(Boolean) livingOak?: boolean;
    @Attribute(Boolean) closable?: boolean;
    @Attribute(String) overline?: string;
    @Attribute(String) aspectRatio?: AspectRatioInput;
    @Attribute(String) alignContent?: 'left' | 'center';
    @Attribute(String) imageSize: 'small' | 'large' = 'large';
    @Attribute(Boolean) columnCard?: boolean;
    @Attribute(Boolean) statCard?: boolean;
    @Attribute(Boolean) callbackCard?: boolean;
    @Attribute(String) background?: UISurfaceColor;
    @Attribute(String) layout: UICardLayout = UICard.Layout.Left;

    @Child('figure') figure!: HTMLElement;
    @Child('article') article!: HTMLElement;
    @Child('ui-buttonbar') buttonbar!: HTMLElement;
    @Child('.ui-card__close-button') closeButton!: UIButton;

    private closeHandler!: EventHandler;

    /**
     * @private
     */
    private getFirstInnerLink(): HTMLAnchorElement | null {
        return this.querySelector('a:first-of-type, ui-link:first-of-type');
    }

    /**
     * @private
     */
    private getLivingOakMaskClasses(): Record<string, boolean> | undefined {
        if (!this.livingOak) {
            return undefined;
        }
        const isMaskTop = this.layout === UICard.Layout.Bottom;
        const isMaskBottom = this.layout !== UICard.Layout.Bottom;
        return {
            '-living-oak-mask': true,
            '-living-oak-mask-top': isMaskTop,
            '-living-oak-mask-bottom': isMaskBottom,
        };
    }

    /**
     * @private
     */
    private renderFigure(): void {
        const figure =
            this.querySelector('figure') ?? document.createElement('figure');

        if (this.image) {
            const imgElement = this.createElement({
                tagName: 'img',
                attributes: {
                    src: this.image,
                    alt: this.alt || '',
                },
                classList: this.getLivingOakMaskClasses(),
            });

            const linkElement = this.getFirstInnerLink();
            const href = linkElement?.getAttribute('href');
            const videoHost = href ? resolveVideoHost(href) : undefined;
            if (videoHost === 'youtube') {
                figure.appendChild(
                    this.createElement({
                        tagName: 'ui-video',
                        attributes: {
                            src: href,
                            poster: this.image,
                        },
                    })
                );
            } else {
                if (this.spotlight) {
                    const imgContainer = this.createElement({
                        tagName: 'div',
                        classList: {
                            'spotlight-container': true,
                        },
                        children: [imgElement],
                    });
                    figure.appendChild(imgContainer);
                } else {
                    figure.appendChild(imgElement);
                }
            }
        }

        [
            'ui-badge',
            'ui-remark',
            'ui-video',
            'ui-shape',
            'ui-icon[size="avatar"]',
        ].forEach((sel) => {
            const elem = this.querySelector(sel);
            if (elem) {
                figure.appendChild(elem);
            }
        });

        if (figure.childNodes.length > 0) {
            this.appendChild(figure);
        }
    }

    /**
     * @private
     */
    private renderArticle(): void {
        let article = this.querySelector('article');
        if (!article) {
            article = this.createElement({
                tagName: 'article',
                children: Array.from(this.childNodes).filter((item) => {
                    return (item as Element).tagName !== 'FIGURE';
                }),
            });
            this.appendChild(article);
        }
        if (this.label) {
            this.setAttribute('aria-label', this.label);
            const title = this.createElement({
                tagName: this.callbackCard ? 'h2' : 'h3',
                children: [this.label],
            });
            article.insertAdjacentElement('afterbegin', title);
        }
        if (this.overline) {
            const overline = this.createElement({
                tagName: 'strong',
                classList: {
                    overline: true,
                },
                children: [this.overline],
            });
            article.insertAdjacentElement('afterbegin', overline);
        }
    }

    /**
     * @private
     */
    private renderCloseButton(): void {
        const button = createElement<UIButton>({
            tagName: 'ui-button',
            attributes: {
                'layout': UIButton.Layout.Tertiary,
                'glyph': 'redesign-x-mark',
                'aria-label': UICard.labels.close,
                'title': UICard.labels.close,
            },
            classList: {
                'ui-card__close-button': true,
            },
        })!;

        this.appendChild(button);
    }

    /**
     * @private
     */
    private handleClose(): void {
        this.dispatchCustomEvent(UICard.CloseEventName);
    }

    /**
     * @private
     */
    private subscribeEvents(): void {
        if (this.callbackCard && this.closable) {
            this.closeButton.addEventListener('click', this.closeHandler);
        }
    }

    /**
     * @private
     */
    private unsubscribeEvents(): void {
        if (this.callbackCard && this.closable) {
            this.closeButton.removeEventListener('click', this.closeHandler);
        }
    }

    render(): void {
        const isImageBottom = this.layout === UICard.Layout.Bottom;
        if (isImageBottom && this.columnCard) {
            this.renderArticle();
            this.renderFigure();
        } else {
            this.renderFigure();
            this.renderArticle();
        }

        if (this.callbackCard && this.closable) {
            this.renderCloseButton();
        }
    }

    hydrate(): void {
        const image = this.figure?.querySelector('img');
        if (image && this.aspectRatio && this.columnCard) {
            UIAspect.applyAspectRatio(image, this.aspectRatio);
        }

        this.closeHandler = this.handleClose.bind(this);
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        this.subscribeEvents();
    }
}
